import type { ConditionEnum } from "@/workflow/components/configurations/data-center/types";
import type { VariableFileName } from "@/workflow/components/configurations/doc-generator/context";
import type { FormAssignment } from "node_modules/@sdt/render-form-engine/dist/types/form";

export type RefName =
  | "initializer-node"
  | "if-node"
  | "calculated-node"
  | "form-node"
  | "switch-node"
  | "assign-group-node"
  | "data-center-node"
  | "expedient-node"
  | "revision-node"
  | "document-generator-node"
  | "sign-document-node"
  | "payment-node"
  | "multithread-node"
  | "wait-node"
  | "notification-node"
  | "interoperability-node"
  | "n8n-node"
  | "resolution-node"
  | "merge-node"
  | "finish-node";
export const NodeTypes = {
  INITIALIZER: "initializer-node",
  IF: "if-node",
  CALCULATED: "calculated-node",
  FORM: "form-node",
  CORRECTION: "revision-node",
  SWITCH: "switch-node",
  ASSIGN_GROUP: "assign-group-node",
  DATA_CENTER: "data-center-node",
  EXPEDIENTE: "expedient-node",
  DOC_GENERATOR: "document-generator-node",
  SIGN_DOCUMENT: "sign-document-node",
  RESOLUTION: "resolution-node",
  N8N: "n8n-node",
  PAYMENT: "payment-node",
  MULTITHREAD: "multithread-node",
  WAIT: "wait-node",
  NOTIFICATION: "notification-node",
  INTEROPERABILITY: "interoperability-node",
  MERGE: "merge-node",
  END: "finish-node",
} as const;

export type Edge = {
  id?: string;
  source?: string | null;
  target?: string | null;
  source_handler: string;
  target_handler: string;
  deleted_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type FilterDataType = "DATE" | "BOOLEAN" | "NUMBER" | "TEXT" | "STRING";

export type Filter = {
  name: string;
  condition: ConditionEnum;
  value: string | null | Array<string>;
  id?: string;
  type?: FilterDataType;
  field?: string;
  output_name?: string;
  connection?:
    | string
    | {
        id: string;
        target_node_id: string | null;
        source_node_id: string | null;
        ref_name: string;
        display_name: string;
        field_id?: string | null;
        output_name?: string;
      };
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
};

export type FilterGroup = {
  id: string;
  filter_node_input_id: string;
  conditions: Array<Filter>;
};

export type Input = Array<{
  id?: string;
  name: string;
  is_sandbox?: boolean;
  node_input_id?: string;
  value:
    | string
    | number
    | null
    | boolean
    | Array<Filter>
    | Array<string>
    | VariableFileName;
  type: string;
  connection?: string;
  field_id?: string; // for variables inside a form
  field?: string; // for variables (legacy format) - UUID for form nodes, endpoint field key for interoperability nodes
  output_name?: string; // for interoperability node outputs - contains the endpoint field key (e.g., "last_name")
  reference_key?: string; // for expediente node - identifies which entity field this input belongs to
  order?: number;
  //if
  conditions?: Conditions;
  calculations?: Calculations;
  references?: Array<{
    name: string;
    type: string;
    value?:
      | string
      | number
      | null
      | boolean
      | Array<string>
      | Array<Filter>
      | VariableFileName;
    connection?: string;
    field?: string;
    field_id?: string;
    output_name?: string;
    global_type?:
      | "APPLICANT"
      | "NODE_EXECUTION_START"
      | "NODE_EXECUTION_FINISH"
      | "CURRENT_TIME";
  }>;
  source_connections?: Array<{
    target_node_input_id?: string;
    field: {
      id: string;
      name: string;
      type: string;
      label?: string;
      form_step_id?: string;
      form_section_id?: string;
      config?: Record<string, string | boolean | number | undefined>;
    };
    source_node_id: string;
    output_name?: string;
    field_id?: string;
    source_node_output_id?: string;
  }>;
  // data center
  label?: string;
  filters?: FilterGroup | Array<Filter>;
  test_node_input?: {
    id: string;
    value: null | string | number | boolean;
    node_input_id: string;
  };
  documents?: Array<{ id: string; inputs: Input }>;
  // interoperability node nested inputs
  inputs?: {
    name: string;
    type: string;
    value: string | number | null | boolean;
    connection?: string;
    field?: string;
    output_name?: string;
    source_connections?: Array<{
      field: {
        id: string;
        name: string;
        type: string;
        config?: Record<string, string | boolean | number | undefined>;
      };
      source_node_id: string;
      output_name?: string;
      field_id?: string;
      source_node_output_id?: string;
    }>;
    field_id?: string;
  };
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
}>;

export type Connections = Record<
  string,
  {
    display_name: string;
    target_node?: string | null;
  }
>;

export type Conditions = Array<{
  id?: string;
  name: string;
  type: string;
  operator?: string;
  inputs: Input;
  input?: Input[number];
  optional_input?: Input[number];
  connection?: string;
  test_node_input?: {
    id: string;
    value: null | string | number | boolean;
    node_input_id: string;
  };
  source_connections?: Array<{
    field: {
      id: string;
    };
    source_node_id: string;
    output_name?: string;
    field_id?: string;
    source_node_output_id?: string;
  }>;
}>;

type Calculations = Array<{
  operation: string;
  inputs: Input & {
    order?: number;
  };
}>;

type ApiConnection = Array<{
  id: string;
  target_node_id: string | null;
  source_node_id: string | null;
  ref_name: string;
  display_name: string;
  field_id?: string | null;
}>;

export type Expiration = {
  id: string;
  display_name: string;
  available_days: ("mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun")[];
  node_id: string;
  created_at: string;
  updated_at: string;
  limit_unit: "minute" | "hour" | "day" | "month" | "year";
  limit_amount: number;
  node_time_extension?: {
    id: string;
    extension_amount: number;
    extension_unit: "minute" | "hour" | "day" | "month" | "year";
    leader_required: boolean;
  };
};

export interface UpdateNodeBody {
  flow_id: string;
  ref_name: RefName;
  display_name: string;
  type: string;
  order: number;
  position_x: number;
  position_y: number;
  manual_continue?: boolean;
  edge?: Edge | null;
  edges?: Array<{
    source: string;
    source_handler: string;
    target_handler: string;
    target_input_name: string;
  }>;
  inputs?: Input;
  connections?: Connections;
}

export interface ApiNode {
  id?: string;
  ref_name: RefName;
  display_name: string;
  position_x: number;
  position_y: number;
  order: number;
  type:
    | "IF_CONDITION"
    | "SWITCH_CONDITION"
    | "GENERAL"
    | "WEBHOOK"
    | "FORM"
    | "REVISION"
    | "DOCUMENT_GENERATOR"
    | "DIGITAL_SIGNATURE"
    | "PAYMENT"
    | "DATA_CENTER"
    | "EXPEDIENTE"
    | "MULTITHREAD"
    | "WAIT"
    | "NOTIFICATION"
    | "INTEROPERABILITY"
    | "SIGN_DOCUMENT"
    | "N8N"
    | "MERGE";
  edge?: Edge;
  edges?: Edge[];
  inputs?: Input;
  expiration?: Expiration;
  manual_continue?: boolean;
  //for switch/if node
  target_connections?: ApiConnection;
  connections?: Connections;
  conditions?: Conditions;
  calculations?: Calculations;
  form_assignments?: FormAssignment[];
}
export interface NodeData {
  type: string;
  node_type: string;
  displayName: string;
  hide_outputs?: boolean;
  inputs: Input;
  order: number;
  edge: {
    source: string;
    source_handler: string;
    target_handler: string;
  } | null;
  edges?: Edge[];
  connections?: Connections;
  outputs?: { id: string; label: string }[];
  conditions?: Conditions;
  calculations?: Calculations;
  manual_continue?: boolean;
}

export interface EdgeData {
  source: {
    id: string;
    handle: string;
  };
  target: {
    id: string;
    handle: string;
  };
}

export interface NodeExecutionTimestamps {
  start_timestamp: string;
  end_timestamp?: string;
  status?: "success" | "error";
  error_message?: string;
}

export const apiConnectionsToConnection = (
  apiConnections: ApiConnection,
): Connections => {
  return apiConnections
    .filter(({ ref_name }) => ref_name)
    .sort((a, b) => {
      // ref_name true should always go first
      if (a.ref_name === "true" && b.ref_name !== "true") return -1;
      if (a.ref_name !== "true" && b.ref_name === "true") return 1;
      return 0;
    })
    .reduce((acc, conn) => {
      acc[conn.ref_name] = {
        display_name: conn.display_name,
        target_node: conn.target_node_id,
      };
      return acc;
    }, {} as Connections);
};

export const getNodeDisplayName = (ref_name: RefName) => {
  switch (ref_name) {
    case NodeTypes.IF:
      return "Operador IF";
    case NodeTypes.SWITCH:
      return "Switch";
    case NodeTypes.FORM:
      return "Formulario";
    case NodeTypes.CALCULATED:
      return "Calculado";
    case NodeTypes.ASSIGN_GROUP:
      return "Asignar grupo";
    case NodeTypes.DATA_CENTER:
      return "Centro de Datos";
    case NodeTypes.EXPEDIENTE:
      return "Expediente Digital";
    case NodeTypes.CORRECTION:
      return "Revisión";
    case NodeTypes.DOC_GENERATOR:
      return "Generador de Documentos";
    case NodeTypes.PAYMENT:
      return "Pasarela de Pago";
    case NodeTypes.SIGN_DOCUMENT:
      return "Firma Digital";
    case NodeTypes.RESOLUTION:
      return "Resolución";
    case NodeTypes.MULTITHREAD:
      return "Multihilos";
    case NodeTypes.WAIT:
      return "Wait";
    case NodeTypes.NOTIFICATION:
      return "Notificación";
    case NodeTypes.INTEROPERABILITY:
      return "Interoperabilidad";
    case NodeTypes.N8N:
      return "Objeto N8N";
    case NodeTypes.END:
      return "Objeto de finalización";
    default:
      return "Desconocido";
  }
};
